import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useReduxState } from 'hooks';
import { useField } from 'informed';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { components, DropdownIndicatorProps } from 'react-select';
import { dateFnsLocales } from 'shared-locales';
import { getFormattedDate } from 'lib/common';
import { DisplayFormattedDate } from 'components/common';
import { Icon } from 'components/common/icons';
import { SelectInput } from 'components/common/form/common';

Object.keys(dateFnsLocales).forEach((key) =>
  registerLocale(key, dateFnsLocales[key])
);

const getTimes = (interval: number) => {
  if (!interval) return [];

  let step = interval;
  const options = ['00:00'];
  while (step < 24 * 60) {
    const hour = Math.floor(step / 60);
    const minutes = step % 60;

    options.push(
      `${hour < 10 ? `0${hour}` : hour}:${
        minutes < 10 ? `0${minutes}` : minutes
      }`
    );
    step += interval;
  }

  return options;
};

const DropdownIndicator = (props: DropdownIndicatorProps) => (
  <components.DropdownIndicator {...props}>
    <Icon
      icon={['far', 'clock']}
      style={{ fontSize: '1.4rem', position: 'relative', top: 1, right: 3 }}
    />
  </components.DropdownIndicator>
);

interface Props {
  additionalCalendarClassName?: string;
  direction?: 'row' | 'column';
  disabled?: boolean;
  // initialTime?: string;
  initialValue?: number;
  isClearable?: boolean;
  isRequired?: boolean;
  field: string;
  id: string;
  maxDate?: Date | string | number;
  minDate?: Date | string | number;
  onChange?: (value: any) => void;
  placeholder?: string;
  style?: CSSProperties;
  usePortal?: boolean;
}

const DatePickerWithTime = (props: Props) => {
  const { language, messages, theme } = useReduxState();

  const [timeInputValue, setTimeInputValue] = useState(
    props.initialValue
      ? getFormattedDate({ date: props.initialValue, dateFormat: 'HH:mm' })
      : '08:00'
  );

  // @ts-ignore
  const { fieldState, fieldApi, render, ref, userProps } = useField<number>({
    ...props,
    ...{ timeInputValue },
    ...(props.isRequired ? { required: messages.commonFieldRequired } : {})
  });

  const {
    additionalCalendarClassName = '',
    direction = 'row',
    disabled = false,
    id,
    isClearable = true,
    maxDate,
    minDate,
    onChange,
    placeholder,
    style,
    usePortal = false
  } = userProps;

  const datePicker = useRef<HTMLDivElement>(undefined);
  const initialTime = useRef(
    getFormattedDate({ date: fieldState.value, dateFormat: 'HH:mm' }) || '08:00'
  ).current;
  const timeFilterOptions = useRef(getTimes(60));

  const [time, setTime] = useState(initialTime);

  useEffect(() => {
    // needed to update initial timeInputValue value from field state
    if (initialTime !== timeInputValue) {
      setTimeInputValue(initialTime);
    }
  }, []);

  useEffect(() => {
    if (typeof fieldState.value !== 'number' || !fieldState.value) {
      return;
    }

    const parsedDate = new Date(fieldState.value);
    const timeFromDate =
      `${parsedDate.getHours()}`.padStart(2, '0') +
      ':' +
      `${parsedDate.getMinutes()}`.padStart(2, '0');
    if (timeFromDate !== time) {
      setTime(timeFromDate);
      setTimeInputValue(timeFromDate);
      // calling setFieldValue should not be needed, but time
      // input id not updating on the first render for some reason
      // so this is a hack to fix it
      setFieldValue(new Date(fieldState.value), timeFromDate);
      return;
    }
  }, [fieldState.value]);

  const handleClear = (ev: React.MouseEvent) => {
    ev.preventDefault();
    setTime(initialTime);
    setTimeInputValue(initialTime);
    setFieldValue(null);
  };

  const handleDateChange = (date: Date) => {
    setFieldValue(date, time);
  };

  const timeStringIsValid = (timeString: string) => {
    return Boolean(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timeString));
  };

  const handleTimeBlur = (ev) => {
    ev.preventDefault();

    let newValue = timeInputValue;
    if (!timeStringIsValid(timeInputValue)) newValue = time;
    if (newValue.length < 5) newValue = `0${newValue}`;

    setTimeInputValue(newValue);
    updateTimeIfValid(newValue);
  };

  const handleTimeChange = (option) => {
    setTime(option.value);
    setTimeInputValue(option.value);
    setFieldValue(new Date(fieldState.value), option.value);
  };

  const handleTimeFocus = () => {
    if (ref.current?.inputRef?.setSelectionRange) {
      setTimeout(() => ref.current.inputRef.setSelectionRange(0, 2));
    }
  };

  const handleTimeInputChange = (inputValue, { action }) => {
    if (action === 'input-blur' || action === 'menu-close') return;

    setTimeInputValue(inputValue);

    updateTimeIfValid(inputValue);

    const caretPosition = ref.current?.inputRef?.selectionStart;
    const caretPositionEnd = ref.current?.inputRef?.selectionEnd;
    if (caretPosition === 2 && caretPositionEnd === 2) {
      ref.current.inputRef.setSelectionRange(3, 5);
    }

    fieldApi.setTouched(true);
  };

  const handleTimeKeyDown = (ev: React.KeyboardEvent) => {
    if (
      ev.key === 'Enter' &&
      (!ref.current?.state.prevProps.menuIsOpen ||
        !ref.current?.state.focusedOption)
    ) {
      ev.preventDefault();
      ref.current.blur();
    } else return;
  };

  const updateTimeIfValid = (value: string) => {
    if (timeStringIsValid(value)) {
      let timeValue = value;

      if (timeValue.length < 5) timeValue = `0${timeValue}`;
      setTime(timeValue);
      setFieldValue(new Date(fieldState.value), timeValue);

      // set focused option to selected option
      const matchingOption = ref.current
        .getFocusableOptions()
        .find((option) => option.value === timeValue);

      setTimeout(() => {
        ref.current?.setState({ focusedOption: matchingOption || null });
      });
    }
  };

  const setFieldValue = (date: Date, time?: string) => {
    let parsedDate = null;

    if (date && time) {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();

      parsedDate = Date.parse(
        `${year}-${month < 10 ? `0${month}` : month}-${
          day < 10 ? `0${day}` : day
        }T${time}`
      );
    }

    fieldApi.setValue(parsedDate);
    fieldApi.validate();
    if (onChange) onChange(parsedDate);
  };

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled
  }) => (
    <div className="datepicker-header-month">
      <button
        className="datepicker-header-month-button"
        disabled={prevMonthButtonDisabled}
        onClick={decreaseMonth}
        type="button"
      >
        <Icon icon="chevron-left" />
      </button>

      <div className="datepicker-header-month-value">
        <DisplayFormattedDate
          date={date}
          dateFormat="LLLL yyyy"
          dateFormatOptions={{ locale: dateFnsLocales[language] }}
        />
      </div>

      <button
        className="datepicker-header-month-button"
        disabled={nextMonthButtonDisabled}
        onClick={increaseMonth}
        type="button"
      >
        <Icon icon="chevron-right" />
      </button>
    </div>
  );

  const { error, value } = fieldState;

  const timeOptions = getTimes(1).map((time) => ({
    value: time,
    label: time
  }));

  return render(
    <div
      style={{
        display: 'flex',
        gap: 10,
        width: '100%',
        ...(direction === 'column'
          ? { flexDirection: 'column', alignItems: 'flex-end' }
          : {})
      }}
    >
      <div className="datepicker" ref={datePicker} style={style}>
        <ReactDatePicker
          calendarClassName={`datepicker-calendar ${additionalCalendarClassName}`}
          dateFormat="dd.MM.yyyy"
          dayClassName={() => 'datepicker-day'}
          disabled={disabled}
          locale={language}
          maxDate={maxDate}
          minDate={minDate}
          onChange={handleDateChange}
          placeholderText={placeholder}
          renderCustomHeader={renderCustomHeader}
          selected={value ? value : null}
          {...(usePortal ? { portalId: 'datepicker-root' } : {})}
        />

        <Icon
          icon={['far', 'calendar-alt']}
          style={{
            color: '#a1a8af',
            fontSize: '1.4rem',
            position: 'absolute',
            right: 11,
            top: 10
          }}
        />

        {isClearable && value && !disabled ? (
          <div className="clear">
            <button
              className="simple-clear"
              aria-label={messages.commonClear}
              data-tooltip={messages.commonClear}
              onClick={handleClear}
            >
              <Icon icon="times-circle" />
            </button>
          </div>
        ) : null}

        {error ? <div className="error-message">{error}</div> : null}

        <style jsx>{`
          .datepicker {
            display: inline-block;
            flex-grow: 1;
            flex-shrink: 1;
            position: relative;
          }
          .datepicker :global(.react-datepicker-wrapper) {
            width: 100%;
          }
          .datepicker :global(.react-datepicker__input-container) {
            background-color: white;
          }
          .datepicker :global(.react-datepicker__input-container input) {
            background-color: transparent;
            border: 1px solid ${error ? theme.dangerColor : theme.lightGreyDark};
            border-radius: ${theme.inputBorderRadius};
            color: ${theme.textColor};
            font-family: inherit;
            font-size: 1.5rem;
            outline: 0;
            padding: 7px 10px;
            position: relative;
            transition: border-color 0.2s;
            width: 100%;
            z-index: 2;
          }
          .datepicker
            :global(
              .react-datepicker__input-container
                input.react-datepicker-ignore-onclickoutside
            ) {
            border-color: ${error ? theme.dangerColor : '#a1a8aa'};
          }
          .datepicker
            :global(.react-datepicker__input-container input:disabled) {
            background-color: ${theme.lightGreyLighter};
            color: ${theme.textColorLighter};
            opacity: 0.7;
          }
          .datepicker
            :global(
              .react-datepicker__input-container
                input::-webkit-input-placeholder
            ) {
            color: #999;
            opacity: 1;
          }
          .datepicker
            :global(
              .react-datepicker__input-container input::-moz-placeholder
            ) {
            color: #999;
            opacity: 1;
          }
          .datepicker
            :global(
              .react-datepicker__input-container input::-ms-input-placeholder
            ) {
            color: #999;
            opacity: 1;
          }
          .datepicker
            :global(
              .react-datepicker__input-container input:-ms-input-placeholder
            ) {
            color: #999;
            opacity: 1;
          }
          .datepicker :global(.react-datepicker-popper),
          :global(.datepicker-root .react-datepicker-popper) {
            margin-top: 8px;
            z-index: 3;
          }
          .datepicker :global(.react-datepicker),
          :global(.datepicker-root .react-datepicker) {
            border: 0;
            border-radius: ${theme.dropdownBorderRadius};
            box-shadow: ${theme.dropdownShadow};
            font-family: inherit;
            font-size: 1.5rem;
            padding: 12px;
          }
          .datepicker :global(.react-datepicker__triangle),
          :global(.datepicker-root .react-datepicker__triangle) {
            display: none;
          }
          .datepicker :global(.react-datepicker__header),
          :global(.datepicker-root .react-datepicker__header) {
            background-color: white;
            border: 0;
            padding: 0;
          }
          .datepicker :global(.datepicker-header-month),
          :global(.datepicker-root .datepicker-header-month) {
            color: ${theme.textColor};
            align-items: center;
            display: flex;
            padding: 0 5px;
          }
          .datepicker :global(.datepicker-header-month-button),
          :global(.datepicker-root .datepicker-header-month-button) {
            background-color: transparent;
            border: 0;
            border-radius: 50%;
            cursor: pointer;
            flex-grow: 0;
            flex-shrink: 0;
            font-size: 1.2rem;
            padding: 5px 0;
            text-align: center;
            transition: background-color 0.2s;
            width: 30px;
            height: 30px;
          }
          .datepicker :global(.datepicker-header-month-button:hover),
          :global(.datepicker-root .datepicker-header-month-button:hover) {
            background-color: ${theme.lightGrey};
          }
          .datepicker :global(.datepicker-header-month-value),
          :global(.datepicker-root .datepicker-header-month-value) {
            color: ${theme.textColorLight};
            flex-grow: 1;
            flex-shrink: 1;
            font-size: 1.5rem;
            font-weight: 700;
            padding-bottom: 1px;
          }
          .datepicker :global(.react-datepicker__current-month),
          :global(.datepicker-root .react-datepicker__current-month) {
            font-size: 1.5rem;
          }
          .datepicker :global(.react-datepicker__month),
          :global(.datepicker-root .react-datepicker__month) {
            margin: 0;
          }
          .datepicker :global(.react-datepicker__day-names),
          :global(.datepicker-root .react-datepicker__day-names) {
            padding: 5px 0 0;
          }
          .datepicker :global(.react-datepicker__day),
          .datepicker :global(.react-datepicker__day-name),
          :global(.datepicker-root .react-datepicker__day),
          :global(.datepicker-root .react-datepicker__day-name) {
            margin: 1px 5px;
            padding: 7px 0;
            text-align: center;
            width: 30px;
            height: 30px;
          }
          .datepicker :global(.react-datepicker__day-name),
          :global(.datepicker-root .react-datepicker__day-name) {
            color: ${theme.textColorLight};
            font-size: 1.3rem;
            font-weight: 700;
          }
          .datepicker :global(.react-datepicker__day),
          :global(.datepicker-root .react-datepicker__day) {
            border-radius: 30px;
            color: ${theme.textColorLight};
            font-size: 1.5rem;
            font-weight: 600;
            outline: 0;
            transition: background-color 0.2s;
          }
          .datepicker :global(.react-datepicker__day:hover),
          :global(.datepicker-root .react-datepicker__day:hover) {
            background-color: ${theme.lightGrey};
          }
          .datepicker :global(.react-datepicker__day--today),
          :global(.datepicker-root .react-datepicker__day--today) {
            background-color: ${theme.lightGrey};
          }
          .datepicker :global(.react-datepicker__day--outside-month),
          :global(.datepicker-root .react-datepicker__day--outside-month) {
            color: #a1a8af;
          }
          .datepicker :global(.react-datepicker__day--selected),
          .datepicker :global(.react-datepicker__day--selected:hover),
          .datepicker :global(.react-datepicker__day--keyboard-selected),
          .datepicker :global(.react-datepicker__day--keyboard-selected:hover),
          :global(.datepicker-root .react-datepicker__day--selected),
          :global(.datepicker-root .react-datepicker__day--selected:hover),
          :global(.datepicker-root .react-datepicker__day--keyboard-selected),
          :global(
              .datepicker-root .react-datepicker__day--keyboard-selected:hover
            ) {
            background-color: ${theme.textColor};
            color: white;
          }
          .datepicker :global(.react-datepicker__day--disabled),
          .datepicker :global(.react-datepicker__day--disabled:hover),
          :global(.datepicker-root .react-datepicker__day--disabled),
          :global(.datepicker-root .react-datepicker__day--disabled:hover) {
            background-color: white;
            color: ${theme.textColor};
            opacity: 0.5;
          }
          .clear {
            line-height: 1;
          }
          .simple-clear {
            background-color: transparent;
            border: 0;
            color: #999;
            cursor: pointer;
            font-size: 1.4rem;
            position: absolute;
            right: 28px;
            top: 9px;
            transition: color 0.3s;
            z-index: 2;
          }
          .simple-clear:hover {
            color: ${theme.textColor};
          }
          .error-message {
            color: ${theme.dangerColor};
            font-size: 1.3rem;
            padding: 3px;
          }
        `}</style>
      </div>

      {fieldState.value && !disabled ? (
        <div className="time-wrapper" style={{ width: '85px' }}>
          <SelectInput
            autoComplete="nope"
            controlShouldRenderValue={false}
            components={{ DropdownIndicator }}
            error={error}
            filterOption={(option: (typeof timeOptions)[0]) =>
              timeFilterOptions.current.includes(option.value)
            }
            inputIsHidden={false}
            inputValue={timeInputValue}
            instanceId={`time-selector-${id}`}
            isOptionSelected={() => false}
            isSearchable={true}
            noOptionsMessage={() => `${messages.commonCreate}...`}
            onBlur={handleTimeBlur}
            onChange={handleTimeChange}
            onFocus={handleTimeFocus}
            onInputChange={handleTimeInputChange}
            onKeyDown={handleTimeKeyDown}
            options={timeOptions}
            selectRef={ref}
            value={timeOptions.find((option) => option.value === time)}
          />

          <style jsx>{`
            .time-wrapper :global(.select .react-select__single-value) {
              top: calc(50% - 1px);
            }
          `}</style>
        </div>
      ) : null}
    </div>
  );
};

export default DatePickerWithTime;
